import React from "react";
import { ExternalLink } from "lucide-react";
import { AppHeader } from "../../../../components/layout/AppHeader";
import { LandingFooter } from "../landing/LandingFooter";
import { FadeUp } from "../landing/FadeUp";
import { useLandingScrollToSection } from "./useLandingScrollToSection";
import type {
  ContractorShowcaseSite,
  RouteMap,
  UserDisplay,
} from "../../types";

type ContractorShowcasePageProps = {
  sites: ContractorShowcaseSite[];
  loggedIn: boolean;
  hasBusiness: boolean;
  routes: RouteMap;
  csrfToken: string;
  page: string;
  userDisplay?: UserDisplay | null;
};

export const ContractorShowcasePage: React.FC<ContractorShowcasePageProps> = ({
  sites,
  loggedIn,
  hasBusiness,
  routes,
  csrfToken,
  page,
  userDisplay,
}) => {
  const scrollToSection = useLandingScrollToSection();

  return (
    <div className="min-h-screen overflow-x-hidden bg-background text-foreground">
      <AppHeader
        variant="app"
        loggedIn={loggedIn}
        hasBusiness={hasBusiness}
        routes={routes}
        csrfToken={csrfToken}
        page={page}
        userDisplay={userDisplay}
      />
      <section className="bg-background pt-20 pb-10 sm:pt-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <FadeUp>
            <span className="inline-flex items-center rounded-full border border-premium-500/40 bg-premium-50 px-3 py-1 text-sm font-semibold text-premium-700">
              Contractor showcase
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
              Live contractor websites on ToolKit
            </h1>
            <p className="mt-5 max-w-3xl text-lg text-muted-foreground">
              Each link is a live contractor site, grouped by trade and town.
              These businesses use ToolKit for local search pages and quote
              requests.
            </p>
          </FadeUp>
        </div>
      </section>
      <section className="border-t border-border bg-card py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          {sites.length === 0 ? (
            <p className="text-center text-muted-foreground">
              No live contractor sites are listed yet. Check back soon.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-xl border border-border bg-background">
              {sites.map((site) => (
                <li key={site.siteUrl}>
                  <a
                    href={site.siteUrl}
                    className="flex flex-col gap-2 px-5 py-4 no-underline transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:flex-row sm:items-center sm:justify-between sm:gap-6"
                  >
                    <span className="font-display text-lg font-semibold text-foreground">
                      {site.anchorLabel}
                    </span>
                    <span className="inline-flex shrink-0 items-center gap-2 text-sm font-medium text-premium-600">
                      Visit site
                      <ExternalLink className="h-4 w-4" aria-hidden />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
      <LandingFooter
        loggedIn={loggedIn}
        hasBusiness={hasBusiness}
        routes={routes}
        scrollToSection={scrollToSection}
      />
    </div>
  );
};
